<template>
	<el-select 
		ref="elSelect"
		v-model="internalStatus" 
		placeholder="Статус заказ-наряда" 
		:persistent="false" 
		:disabled="disabled"
		@change="onDropdownStatusSelected"
		@visible-change="onDropdownVisibilityChange">
			<template #empty>
				{{ dropdownMenuLabel }}
			</template>
			<template #label>
				{{ dropdownMenuLabel }}
			</template>
			<el-option v-for="item in dropdownStatusesList" :key="item.id" :label="item.name" :value="item.id">
				{{ item.name }}
			</el-option>
			<template #footer v-if="cancellable && (dropdownStatusesList?.length || initialStatus != status)">
				<el-link 
					style="font-size: .9rem;" 
					:type="initialStatus == status ? 'info' : 'warning'" 
					:disabled="initialStatus == status" 
					:underline="false" 
					@click="onDropdownCancelChangesClick">
						отменить изменения
					</el-link>
			</template>
	</el-select>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { ElSelect, ElIcon } from 'element-plus';
import { ArrowDown } from '@element-plus/icons-vue';
import OrderStatusComponent from './OrderStatusComponent.vue';
import OrderStatus from '@/models/enums/OrderStatus';
import OrderStatusModel from '@/models/OrderStatusModel';
import store from '@/store';
import { watch, WatchStopHandle } from 'vue';
import orderStatusesHelper from '@/helpers/orderStatusHelper';

@Options({
	components: {
		ElIcon,
		ElSelect, ArrowDown,
		OrderStatusComponent
	},
	props: {
		status: {
			type: Number,
			require: true
		},
		statuses: {
			type: Array,
			default: null,
			require: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		parentScrollableElement: {
			type: HTMLElement,
			default: null
		},
		/*allow passing through statuses flow after current status changed*/
		forwardFlow: {
			type: Boolean,
			default: false
		},
		cancellable: {
			type: Boolean,
			default: false
		}
	},
	emits: ['change']
})
export default class OrderStatusSelectorComponent extends Vue {
	/** props */	
	status!: number;
	disabled!: boolean;
	statuses!: Array<number>;
	parentScrollableElement!: HTMLElement;
	/*allow passing through statuses flow after current status changed*/
	forwardFlow!: boolean;
	cancellable!: boolean;

	/** view-models */
	internalStatus = 0;
	initialStatus: number | null = null;
	initialStatusDescription: string | null = null;

	dropdownMenuType = 'warning';
	dropdownMenuLabel = '';
	dropdownStatusesList: Array<OrderStatusModel> | null = null;

	statusWathSubscription: WatchStopHandle | null = null;
	predefinedStatusesWatchSubscription: WatchStopHandle | null = null;
	
	mounted(): void {
		this.initialStatus = this.status;
		this.initialStatusDescription = this.getDropdownMenuLabel(this.initialStatus);
		this.refreshComponent();

		this.statusWathSubscription = watch(
			() => this.status,
			() => this.refreshComponent()
		);
		
		this.predefinedStatusesWatchSubscription = watch(
			() => this.statuses,
			(v) => this.dropdownStatusesList = this.getDropdownMenuItems(v));		
	}

	unmounted(): void {
		if (this.statusWathSubscription) {
			this.statusWathSubscription();
		}

		if (this.predefinedStatusesWatchSubscription) {
			this.predefinedStatusesWatchSubscription();
		}
	}

	/** syncs select v-model with external status; sets component label, items list and type (type is not implemented) */
	refreshComponent() {
		const predefinedStatuses = this.statuses;

		this.internalStatus = this.status;
		this.dropdownMenuType = this.getDopdownMenuType(this.status);
		this.dropdownMenuLabel = this.getDropdownMenuLabel(this.status);
		this.dropdownStatusesList = this.getDropdownMenuItems(predefinedStatuses);
	}

	onDropdownVisibilityChange(dropdownVisible: boolean) {			
		const parentScollableElement = this.parentScrollableElement ? this.parentScrollableElement : window;
		if (dropdownVisible) {
			parentScollableElement.addEventListener('scroll', this.parentElementScollEventHandler);
		} else {
			parentScollableElement.removeEventListener('scroll', this.parentElementScollEventHandler);
		}
	}

	onDropdownStatusSelected() {
		this.$nextTick()
			.then(() => {				
				if (this.forwardFlow) {
					this.dropdownStatusesList = this.getDropdownMenuItems(null);
				}

				this.dropdownMenuType = this.getDopdownMenuType(this.internalStatus);
				this.dropdownMenuLabel = this.getDropdownMenuLabel(this.internalStatus);
				this.$emit('change', this.internalStatus);
			});
	}

	getDopdownMenuType(statusId: number) {
		switch(statusId) {
			default:
			case OrderStatus.PartsOrdering:
			case OrderStatus.PartsAwaiting:
			case OrderStatus.VisitNegotiating:
			case OrderStatus.ExtraNegotiating:
				return 'warning';

			case OrderStatus.VisitAwaiting:
			case OrderStatus.VehicleAccepted:
			case OrderStatus.VehicleInProgress:
				return 'primary';

			case OrderStatus.VehicleCompleted:
				return 'success';

			case OrderStatus.Closed:
				return 'info';

			case OrderStatus.Refuse:
				return 'danger';
		}
	}

	getDropdownMenuLabel(statusId: number) {
		switch(statusId) {
			default:
			case OrderStatus.PartsOrdering: return 'Заказ запчастей';
			case OrderStatus.PartsAwaiting: return 'Ожидание запчастей';
			case OrderStatus.VisitNegotiating: return 'Согласование визита';
			case OrderStatus.ExtraNegotiating: return 'Доп. согласование';
			case OrderStatus.VisitAwaiting: return 'Ожидание визита';
			case OrderStatus.VehicleAccepted: return 'Автомобиль принят';
			case OrderStatus.VehicleInProgress: return 'Выполнение работ';
			case OrderStatus.VehicleCompleted: return 'Работы выполнены';
			case OrderStatus.Closed: return 'Закрыт';
			case OrderStatus.Refuse: return 'Отказ';
		}
	}

	getDropdownMenuItems(predefinedStatuses: Array<number> | null) : Array<OrderStatusModel> {
		const statuses = store.getters.orderStatuses as Array<OrderStatusModel>;
		const statusesDescriptions = orderStatusesHelper.statusesDescriptionDict() as any;
		if (!predefinedStatuses) {
			return statuses.find(_ => _.id == this.status)?.subStatuses?.map(_ => new OrderStatusModel({
				id: _.id,
				name: statusesDescriptions[_.id]
			})) ?? [];
		} else {
			return statuses.filter(s => this.statuses.some(_ => _ == s.id)).map(_ => new OrderStatusModel({
				id: _.id,
				name: statusesDescriptions[_.id]
			}));
		}
	}

	parentElementScollEventHandler() {
		const selectComponent = this.$refs.elSelect as any;
		selectComponent?.blur();
	}

	onDropdownCancelChangesClick() {
		this.dropdownMenuType = this.getDopdownMenuType(this.initialStatus!);
		this.dropdownMenuLabel = this.getDropdownMenuLabel(this.initialStatus!);
		this.$emit('change', this.initialStatus!);
		return;
	}
}
</script>

<style>
.dropdown-disabled {
	cursor: default;
	color: var(--el-disabled-text-color); 	
	background: var(--el-disabled-border-color);
}

	.dropdown-disabled .order-status_dropdown_label_container {
		cursor: default;
	}

.order-status_dropdown_label_container {
	cursor: pointer;
}

	.order-status_dropdown_label_container .order-status_dropdown_label {
		font-size: .92rem;
	}

		.order-status_dropdown_label_container .order-status_dropdown_label .order-status_dropdown_label_internal {
			display: flex; 
			align-items: center
		}

			.order-status_dropdown_label_container .order-status_dropdown_label .order-status_dropdown_label_internal .icon {
				width: 1.0666rem; 
				height: 1.0666rem;
				transform: rotate(0);
				transition: transform ease-in-out .3s;
			}

				.order-status_dropdown_label_container .order-status_dropdown_label .order-status_dropdown_label_internal .icon.rotated {
					transform: rotate(180deg);
				}
</style>